import React, { useState, useMemo } from 'react';
import Box from '@mui/material/Box';
import {
  Card,
  CardContent,
  Grid,
  TextField,
  Typography,
} from '@mui/material';
import Autocomplete from '@mui/material/Autocomplete';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Page, Text, View, Document, PDFViewer } from '@react-pdf/renderer';
import { useMaterialUIController } from 'context';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useTheme } from '@mui/material/styles';
import SearchIcon from '@mui/icons-material/Search';
import DescriptionIcon from '@mui/icons-material/Description';
import CancelIcon from '@mui/icons-material/Cancel';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import FileDownloadRoundedIcon from '@mui/icons-material/FileDownloadRounded';
import { FP, PageHeader, fpDataGridSx } from "components/financepro";
import { useApi, useSucursales } from "lib/api";

function StatCard({ label, value, icon: Icon, accent }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: '100%',
        borderRadius: 2.5,
        border: `1px solid ${FP.color.border}`,
        bgcolor: FP.color.cardBg,
        boxShadow: FP.shadow.soft,
        overflow: 'hidden',
        position: 'relative',
        transition: 'all 0.2s ease',
        '&:hover': { boxShadow: FP.shadow.md, transform: 'translateY(-2px)' },
        '&::before': {
          content: '""',
          position: 'absolute',
          left: 0,
          top: 0,
          bottom: 0,
          width: 4,
          bgcolor: accent,
        },
      }}
    >
      <CardContent sx={{ p: 2.4, '&:last-child': { pb: 2.4 } }}>
        <Box display="flex" justifyContent="space-between" alignItems="flex-start" gap={1.5}>
          <Box flex={1} minWidth={0}>
            <Typography
              sx={{
                color: FP.color.textSecondary,
                fontWeight: 700,
                letterSpacing: '0.1em',
                textTransform: 'uppercase',
                fontSize: '0.66rem',
                mb: 1,
              }}
            >
              {label}
            </Typography>
            <Typography
              sx={{
                fontWeight: 800,
                color: FP.color.textPrimary,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.2,
                letterSpacing: '-0.02em',
                fontSize: '1.4rem',
                wordBreak: 'break-word',
              }}
            >
              {value}
            </Typography>
          </Box>
          <Box
            sx={{
              flexShrink: 0,
              width: 42,
              height: 42,
              borderRadius: 1.4,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              bgcolor: `${accent}14`,
              color: accent,
            }}
          >
            <Icon sx={{ fontSize: 20 }} />
          </Box>
        </Box>
      </CardContent>
    </Card>
  );
}

const INPUT_SX = {
  '& .MuiOutlinedInput-root': {
    height: 46,
    minHeight: 46,
    borderRadius: 1.6,
    bgcolor: '#FFFFFF',
    '& fieldset': { borderColor: FP.color.border },
    '&:hover fieldset': { borderColor: FP.color.borderStrong },
    '&.Mui-focused fieldset': { borderColor: FP.color.navy },
  },
  '& .MuiInputLabel-root': { fontSize: '0.86rem' },
};

function buildAuditoriaColumns() {
  return [
    { field: 'id', headerName: 'DOC #', width: 85, align: 'center', headerAlign: 'center' },
    { field: 'fecha', headerName: 'Fecha', type: 'string', width: 110, align: 'center', headerAlign: 'center', editable: true },
    { field: 'nombres', headerName: 'Nombres', flex: 1, minWidth: 130, editable: true },
    { field: 'apellidos', headerName: 'Apellidos', type: 'string', flex: 1, minWidth: 130, editable: true },
    { field: 'cantsol', headerName: 'Cantidad', type: 'string', width: 120, align: 'right', headerAlign: 'right' },
    { field: 'mora', headerName: 'Mora', type: 'string', width: 100, align: 'right', headerAlign: 'right' },
  ];
}

function Auditoriarecibos() {
  const [fecha1, setFecha1] = useState('');
  const [fecha2, setFecha2] = useState('');
  const [nosucursal, setNosucursal] = useState('');
  const [rows, setRows] = useState([]);
  const [presentarpdf, setPresentarpdf] = useState(false);

  const [controller] = useMaterialUIController();
  const { nombreNegocio } = controller;
  const { post } = useApi();
  const sucursal = useSucursales();
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));

  const auditoriaColumns = useMemo(() => buildAuditoriaColumns(), []);

  async function procesarrequerimiento() {
    const { status, data } = await post('/api/auditoriarecibos', {
      recibodesde: fecha1,
      recibohasta: fecha2,
      sucursal: nosucursal,
    });
    if (status === 200) {
      setRows(data["recibos"])
    }
    if (status === 400) {
      setRows([])
      alert(data["Error"])
    }
  }

  const MyDocument = ({ rowsData, nombreNegocio1 }) => {
    const totalMora = (rowsData || []).reduce((sum, row) => sum + (parseFloat(String(row.mora || 0).replace(",", "").trim()) || 0), 0);
    const totalCantidad = (rowsData || []).reduce((sum, row) => sum + (parseFloat(String(row.cantsol || 0).replace(",", "").trim()) || 0), 0);
    return (
      <PDFViewer style={{ height: isXs ? 'calc(100vh - 120px)' : '550px', width: '100%' }}>
        <Document>
          <Page size="A4" orientation="Portrait" wrap style={{ paddingBottom: 65 }}>
            <View style={{ textAlign: 'center', width: '94%', fontSize: 10, textTransform: 'uppercase', marginTop: '15px' }} fixed>
              <Text>{nombreNegocio1 || 'Empresa'}</Text>
            </View>
            <View style={{ textAlign: 'center', width: '94%', fontSize: 9, textTransform: 'uppercase' }} fixed>
              <Text>Auditoria de Recibos</Text>
            </View>
            <View style={{ display: 'flex', alignItems: 'center', flexDirection: 'row', justifyContent: 'left', width: '94%', height: '25px', fontSize: 10, backgroundColor: '#1976d2', color: 'white', marginLeft: '15px' }} fixed>
              <Text style={{ width: '65px', textAlign: 'left', fontWeight: 'bold' }}>DOC #</Text>
              <Text style={{ width: '85px', textAlign: 'left', fontWeight: 'bold' }}>Fecha</Text>
              <Text style={{ width: '140px', textAlign: 'left', fontWeight: 'bold' }}>Nombres</Text>
              <Text style={{ width: '140px', textAlign: 'left', fontWeight: 'bold' }}>Apellidos</Text>
              <Text style={{ width: '110px', textAlign: 'right', fontWeight: 'bold' }}>Cantidad</Text>
              <Text style={{ width: '110px', textAlign: 'right', fontWeight: 'bold' }}>Mora</Text>
            </View>
            {(rowsData || []).map((filas, i) => (
              <View key={i} style={{ flexDirection: 'row', width: '94%', height: '15px', fontSize: 9, color: 'black', marginLeft: '15px', backgroundColor: i % 2 === 0 ? '#f9f9f9' : 'white' }}>
                <Text style={{ width: '65px', textAlign: 'left' }}>{filas.id}</Text>
                <Text style={{ width: '85px', textAlign: 'left' }}>{filas.fecha}</Text>
                <Text style={{ width: '140px', textAlign: 'left' }}>{filas.nombres}</Text>
                <Text style={{ width: '140px', textAlign: 'left' }}>{filas.apellidos}</Text>
                <Text style={{ width: '110px', textAlign: 'right' }}>{filas.cantsol}</Text>
                <Text style={{ width: '110px', textAlign: 'right' }}>{filas.mora}</Text>
              </View>
            ))}
            <Text> </Text>
            <View style={{ display: 'flex', flexDirection: 'row', width: '94%', height: '35px', fontSize: 10, backgroundColor: '#f5f5f5', color: 'black', marginLeft: '15px', fontWeight: 'bold', borderTop: '2px solid #1976d2' }}>
              <Text style={{ width: '65px', textAlign: 'left' }}>Total</Text>
              <Text style={{ width: '85px', textAlign: 'left' }}> </Text>
              <Text style={{ width: '140px', textAlign: 'left' }}> </Text>
              <Text style={{ width: '140px', textAlign: 'left' }}> </Text>
              <Text style={{ width: '110px', textAlign: 'right' }}>{totalCantidad.toFixed(2)}</Text>
              <Text style={{ width: '110px', textAlign: 'right' }}>{totalMora.toFixed(2)}</Text>
            </View>
          </Page>
        </Document>
      </PDFViewer>
    );
  };

  function handlepdf() {
    if (rows.length === 0) {
      alert("No hay datos para procesar este reporte")
    } else {
      setPresentarpdf(true)
    }
  }

  const totalMora = rows.reduce((sum, row) => sum + (parseFloat(String(row.mora || 0).replace(",", "").trim()) || 0), 0);
  const totalCantidad = rows.reduce((sum, row) => sum + (parseFloat(String(row.cantsol || 0).replace(",", "").trim()) || 0), 0);

  return (
    <Box
      component="main"
      sx={{
        minHeight: '100vh',
        bgcolor: FP.color.surface,
      }}
    >
      <PageHeader
        title="Pagos"
        subtitle="Auditoría de Recibos"
        cta={
          <Button
            variant="contained"
            startIcon={<FileDownloadRoundedIcon />}
            disabled={rows.length === 0}
            onClick={handlepdf}
            sx={{
              bgcolor: FP.color.navy,
              color: '#FFFFFF',
              borderRadius: 1.4,
              px: 2.2,
              height: 38,
              boxShadow: 'none',
              textTransform: 'none',
              fontWeight: 700,
              '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
              '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' },
            }}
          >
            Export Data
          </Button>
        }
      />

      <Box sx={{ p: { xs: 2, sm: 2.5, md: 3 } }}>
        <Card
          elevation={0}
          sx={{
            mb: 3,
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              gap: 1.2,
            }}
          >
            <Box
              sx={{
                width: 30,
                height: 30,
                borderRadius: 1.2,
                bgcolor: FP.color.accentBlueSoft,
                color: FP.color.accentBlue,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <SearchIcon sx={{ fontSize: 18 }} />
            </Box>
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Filtros de Búsqueda
            </Typography>
          </Box>
          <CardContent sx={{ p: { xs: 2, md: 3 } }}>
            <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  type="number"
                  label="Recibo Desde"
                  value={fecha1}
                  onChange={(event) => setFecha1(event.target.value)}
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={INPUT_SX}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <TextField
                  type="number"
                  label="Recibo Hasta"
                  value={fecha2}
                  onChange={(event) => setFecha2(event.target.value)}
                  variant="outlined"
                  fullWidth
                  InputLabelProps={{ shrink: true }}
                  sx={INPUT_SX}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={4}>
                <Autocomplete
                  disablePortal
                  options={sucursal || []}
                  value={nosucursal || null}
                  onChange={(event, value) => setNosucursal(value)}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Sucursal"
                      fullWidth
                      InputLabelProps={{ shrink: true }}
                      sx={INPUT_SX}
                    />
                  )}
                  fullWidth
                />
              </Grid>
            </Grid>
            <Box display="flex" justifyContent="flex-end" sx={{ mt: 2.5 }}>
              <Button
                variant="contained"
                startIcon={<SearchIcon />}
                onClick={() => procesarrequerimiento()}
                sx={{
                  width: { xs: '100%', sm: 'auto' },
                  minWidth: { sm: 200 },
                  height: 46,
                  bgcolor: FP.color.navy,
                  color: '#FFFFFF',
                  borderRadius: 1.4,
                  textTransform: 'none',
                  fontSize: '0.94rem',
                  fontWeight: 700,
                  boxShadow: 'none',
                  '&:hover': { bgcolor: FP.color.navyDeep, boxShadow: 'none' },
                }}
              >
                Procesar
              </Button>
            </Box>
          </CardContent>
        </Card>

        {rows.length > 0 && (
          <Grid container spacing={2} sx={{ mb: 3 }}>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total de registros"
                value={String(rows.length)}
                icon={DescriptionIcon}
                accent={FP.color.accentBlue}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total cantidad"
                value={`$${totalCantidad.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={AttachMoneyIcon}
                accent={FP.color.successDark}
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <StatCard
                label="Total mora"
                value={`$${totalMora.toLocaleString('es-DO', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
                icon={TrendingUpIcon}
                accent={FP.color.warning}
              />
            </Grid>
          </Grid>
        )}

        <Card
          elevation={0}
          sx={{
            borderRadius: 2.5,
            border: `1px solid ${FP.color.border}`,
            bgcolor: FP.color.cardBg,
            boxShadow: FP.shadow.soft,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              px: { xs: 2, md: 3 },
              py: 1.6,
              borderBottom: `1px solid ${FP.color.border}`,
              bgcolor: '#FAFBFE',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 1,
              flexWrap: 'wrap',
            }}
          >
            <Typography sx={{ fontWeight: 700, color: FP.color.textPrimary, fontSize: '0.98rem' }}>
              Listado de Recibos
            </Typography>
            {rows.length > 0 && (
              <Button
                variant="outlined"
                size="small"
                startIcon={<PictureAsPdfIcon />}
                onClick={() => handlepdf()}
                sx={{
                  borderColor: FP.color.danger,
                  color: FP.color.danger,
                  borderRadius: 1.2,
                  textTransform: 'none',
                  fontWeight: 700,
                  '&:hover': { bgcolor: FP.color.dangerSoft, borderColor: FP.color.danger },
                }}
              >
                Generar PDF
              </Button>
            )}
          </Box>
          <CardContent sx={{ p: { xs: 1.5, sm: 2 }, '&:last-child': { pb: { xs: 1.5, sm: 2 } } }}>
            {rows.length > 0 ? (
              <>
                <Box sx={{ display: { xs: 'none', md: 'block' }, width: '100%', overflowX: 'auto' }}>
                  <DataGrid
                    rows={rows}
                    columns={auditoriaColumns}
                    getRowId={(row) => (row.id != null ? String(row.id) : `${row.fecha}-${row.nombres}-${row.apellidos}`)}
                    pageSizeOptions={[5, 10, 25, 50]}
                    initialState={{ pagination: { paginationModel: { pageSize: 10 } } }}
                    checkboxSelection
                    disableRowSelectionOnClick
                    rowHeight={42}
                    sx={{
                      minWidth: { xs: 640, sm: '100%' },
                      height: 520,
                      ...fpDataGridSx,
                    }}
                    autoHeight={false}
                  />
                </Box>
                <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', gap: 1.4 }}>
                  {rows.map((row, idx) => (
                    <Box
                      key={row.id != null ? String(row.id) : `card-${idx}`}
                      sx={{
                        border: `1px solid ${FP.color.border}`,
                        borderRadius: 2,
                        p: 1.5,
                        bgcolor: '#FFFFFF',
                        boxShadow: FP.shadow.soft,
                      }}
                    >
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 1, mb: 1 }}>
                        <Box sx={{ minWidth: 0 }}>
                          <Typography sx={{ fontWeight: 800, color: FP.color.textPrimary, fontSize: '1rem', lineHeight: 1.2 }}>
                            Recibo #{row.id}
                          </Typography>
                          <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.78rem', mt: 0.25, wordBreak: 'break-word' }}>
                            {row.fecha} · {[row.nombres, row.apellidos].filter(Boolean).join(' ')}
                          </Typography>
                        </Box>
                      </Box>
                      <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0,1fr))', gap: 1 }}>
                        {[
                          ['Cantidad', row.cantsol],
                          ['Mora', row.mora],
                        ].map(([label, value]) => (
                          <Box key={label}>
                            <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.66rem', fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase' }}>
                              {label}
                            </Typography>
                            <Typography sx={{ color: FP.color.textPrimary, fontSize: '0.92rem', fontWeight: 600, fontVariantNumeric: 'tabular-nums', wordBreak: 'break-word' }}>
                              {value ?? '—'}
                            </Typography>
                          </Box>
                        ))}
                      </Box>
                    </Box>
                  ))}
                </Box>
              </>
            ) : (
              <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={8}>
                <DescriptionIcon sx={{ fontSize: 56, color: FP.color.textMuted, mb: 2 }} />
                <Typography sx={{ color: FP.color.textPrimary, fontWeight: 700 }} gutterBottom>
                  No hay datos para mostrar
                </Typography>
                <Typography sx={{ color: FP.color.textSecondary, fontSize: '0.9rem' }} textAlign="center" px={2}>
                  Complete los filtros y haga clic en "Procesar" para obtener resultados.
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>

        {presentarpdf && (
          <Dialog
            open={presentarpdf}
            maxWidth="lg"
            fullWidth
            fullScreen={isXs}
            onClose={() => setPresentarpdf(false)}
            PaperProps={{ sx: { borderRadius: { xs: 0, sm: 2 } } }}
          >
            <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <Typography variant="h6" fontWeight="bold">
                Vista Previa del Reporte PDF
              </Typography>
              <Button onClick={() => setPresentarpdf(false)} sx={{ minWidth: 'auto', p: 1 }}>
                <CancelIcon />
              </Button>
            </DialogTitle>
            <DialogContent>
              <MyDocument rowsData={rows} nombreNegocio1={nombreNegocio} />
            </DialogContent>
          </Dialog>
        )}
      </Box>
    </Box>
  );
}
export default Auditoriarecibos
